/**
 * LOKASI SSO Utilities
 *
 * Storage + URL helpers for the LOKASI internal SSO flow (OAuth 2.0
 * authorization code + PKCE against the LOKASI IdP, followed by a
 * `token_exchange` against this app's own BFF).
 */

import { CheckProviderResponse } from "@/types/auth";

import { clearPkceParams } from "./pkceUtils";

const PROVIDER_KEY = "sso_provider";

/**
 * `VITE_BASE_PATH` is authored with a trailing slash in this app
 * (`/intelligence/`), so strip it before concatenating path segments.
 */
export const getBasePath = (): string =>
  (import.meta.env.VITE_BASE_PATH || "").replace(/\/+$/, "");

/**
 * Cache the provider config resolved at `startSso` so the callback can exchange
 * the short-lived `code` immediately, without re-fetching it (which would add
 * latency and risk the code expiring before the exchange).
 */
export const storeProviderConfig = (provider: CheckProviderResponse): void => {
  sessionStorage.setItem(PROVIDER_KEY, JSON.stringify(provider));
};

export const getStoredProviderConfig = (): CheckProviderResponse | null => {
  const raw = sessionStorage.getItem(PROVIDER_KEY);
  if (!raw) return null;

  try {
    return JSON.parse(raw) as CheckProviderResponse;
  } catch {
    return null;
  }
};

export const clearProviderConfig = (): void => {
  sessionStorage.removeItem(PROVIDER_KEY);
};

/**
 * Drop everything tied to a single authorization attempt. Runs on both success
 * and failure so a leaked verifier is never reusable: every retry mints a fresh
 * PKCE pair.
 */
export const clearSsoParams = (): void => {
  clearPkceParams();
  clearProviderConfig();
};

/**
 * The SSO callback URL on this origin. Derived from `location.origin` so it
 * matches localhost in dev and the deployed origin in production — but every
 * origin must be registered with the IdP.
 */
export const getSsoCallbackUrl = (): string =>
  `${globalThis.location.origin}${getBasePath()}/sso/callback`;

/**
 * Anti open-redirect control. `redirect_to` arrives from the URL, so it must be
 * proven same-origin before any navigation is built from it.
 */
export const isSameOriginUrl = (url: string): boolean => {
  try {
    return (
      new URL(url, globalThis.location.origin).origin ===
      globalThis.location.origin
    );
  } catch {
    return false;
  }
};
